import { useTranslation } from "react-i18next";
import { Maximize2, Pause, Play, Square } from "lucide-react";
import { useNavigate } from "react-router";
import { Button } from "../ui";
import type { DocHandle } from "../sync/docManager";
import { useDocSelector } from "../core/useDocSelector";
import { useNow } from "../core/useNow";
import { readTask } from "../core/tasks";
import { fmtElapsed } from "../core/format";
import { elapsedMs, readCurrent, stopTracking } from "./timelog";

/** App-bar timer: current task, elapsed, stop. The kiosk-sized widget is `tracking-status` (M3). */
import { pauseTimer, resumeTimer } from "./linked";
export function TrackingStatus({
  core,
  workspaceId,
}: {
  core: DocHandle | null;
  workspaceId?: string | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const now = useNow();
  const current = useDocSelector(core, (doc) => readCurrent(doc));
  const title = useDocSelector(core, (doc) => {
    const cur = readCurrent(doc);
    return cur ? (readTask(doc, cur.taskId)?.title ?? "") : "";
  });
  if (!core || !current) return null;
  return (
    <div
      className="flex items-center gap-2 rounded-sm border border-neutral-200 bg-[var(--surface)] py-1 pl-3 pr-1 text-[13px] dark:border-neutral-800"
      data-testid="tracking-status"
    >
      <button
        type="button"
        className="max-w-[12rem] truncate hover:text-accent"
        onClick={() => navigate(`/t/${current.taskId}`)}
      >
        {title || t("task.untitled")}
      </button>
      <span
        className="font-mono tabular-nums text-neutral-500"
        data-testid="tracking-elapsed"
        data-paused={!!current.pausedAt}
        title={current.pausedAt ? t("tracking.paused") : undefined}
      >
        {fmtElapsed(elapsedMs(current, now))}
        {current.pausedAt && <span className="ml-1 font-sans">· {t("tracking.paused")}</span>}
      </span>
      <Button
        variant="ghost"
        size="sm"
        aria-label={current.pausedAt ? t("tracking.resume") : t("tracking.pause")}
        title={current.pausedAt ? t("tracking.resume") : t("tracking.pause")}
        className="w-[30px] px-0 touch:h-11 touch:w-11"
        onClick={() =>
          void (current.pausedAt ? workspaceId && resumeTimer(core.doc, workspaceId) : pauseTimer(core.doc))
        }
        data-testid="tracking-pause"
      >
        {current.pausedAt ? <Play size={13} aria-hidden /> : <Pause size={13} aria-hidden />}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        aria-label={t("bigClock.open")}
        title={t("bigClock.open")}
        className="w-[30px] px-0 touch:h-11 touch:w-11"
        onClick={() => navigate(current ? `/focus/${current.taskId}?large=1` : "/focus")}
        data-testid="tracking-big-view"
      >
        <Maximize2 size={13} aria-hidden />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        aria-label={t("tracking.stop")}
        className="w-[30px] px-0 touch:h-11 touch:w-11"
        onClick={() => void stopTracking(core.doc)}
      >
        <Square size={13} aria-hidden />
      </Button>
    </div>
  );
}
